import { observer } from 'mobx-react-lite';
import { useResolve } from '../use';
import { TOKEN_MoverStore, type MoverStore } from './MoverStore';
import { COURTS, type Court } from '@/domain/session';

// The mover's screen: the tick's move list, the court they are working, and the full audit.
//
// The user is walking a noisy hall reading three-digit numbers off arms and bibs, at a distance,
// while somebody talks to them. Everything here is sized for that: the player number is the biggest
// thing on every row, the from-court and to-court are colour chips rather than words to parse, and
// the button that actions a row is a full-height target on the right where a thumb already is.
//
// Legible beats pretty, deliberately. The goal puts anything visual beyond legible out of scope.

const COURT_TONE: Record<Court, string> = {
  white: 'bg-neutral-200 text-neutral-900',
  red: 'bg-rose-600 text-white',
  purple: 'bg-purple-600 text-white',
  gold: 'bg-amber-500 text-neutral-900',
};

export const MoverView = observer(function MoverView() {
  const mover = useResolve(TOKEN_MoverStore);

  return (
    <div className="flex flex-1 flex-col gap-3 p-3">
      <CourtPicker mover={mover} />
      {/* Why a button is dead. Above the list rather than beside it: a mover who cannot tick a row
          off needs to know before they have walked a player across the hall. */}
      {mover.notice !== undefined && (
        <p
          role="status"
          className="rounded-lg bg-neutral-800 p-3 text-center text-lg text-neutral-200 ring-1 ring-neutral-700"
        >
          {mover.notice}
        </p>
      )}
      {/* A failed write must be visibly failed (docs/sync.md). A silently dropped tick-off leaves
          a player standing on the wrong court with this screen saying they moved. */}
      {mover.failedMessage !== undefined && (
        <p
          role="alert"
          className="rounded-lg bg-amber-950 p-3 text-center text-lg text-amber-100 ring-1 ring-amber-800"
        >
          {mover.failedMessage}
        </p>
      )}
      <Confirm mover={mover} />
      <Tabs mover={mover} />
      {mover.panel === 'moves' && <Moves mover={mover} />}
      {mover.panel === 'court' && <CourtPanel mover={mover} />}
      {mover.panel === 'audit' && <Audit mover={mover} />}
    </div>
  );
});

// Which court this mover is standing on. A view change with one side effect: it decides which
// court their own votes count against (docs/rotation.md).
const CourtPicker = observer(function CourtPicker({ mover }: { mover: MoverStore }) {
  return (
    <header className="flex gap-2">
      {COURTS.map((court) => (
        <button
          key={court}
          aria-pressed={mover.court === court}
          onClick={() => mover.openCourt(court)}
          className={`flex-1 rounded-lg py-2 text-lg font-bold uppercase ${COURT_TONE[court]} ${
            mover.court === court ? 'ring-4 ring-sky-400' : 'opacity-60'
          }`}
        >
          {court}
          {/* The live headcount: the mover is the person evening these numbers out. */}
          <span className="block text-sm font-normal tabular-nums">{mover.courtCounts[court]}</span>
        </button>
      ))}
    </header>
  );
});

const Tabs = observer(function Tabs({ mover }: { mover: MoverStore }) {
  const tabs = [
    { panel: 'moves' as const, label: `Moves (${mover.outstandingRows.length})` },
    {
      panel: 'court' as const,
      label: mover.court === undefined ? 'Court' : `${mover.court} court`,
    },
    { panel: 'audit' as const, label: `Audit (${mover.auditRows.length})` },
  ];
  return (
    <nav className="grid grid-cols-3 gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.panel}
          aria-pressed={mover.panel === tab.panel}
          onClick={() => mover.openPanel(tab.panel)}
          className={`rounded-lg py-3 text-lg font-semibold capitalize ${
            mover.panel === tab.panel
              ? 'bg-neutral-200 text-neutral-900'
              : 'bg-neutral-800 text-neutral-400'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
});

// The tick's list. Immutable from the moment it appears, so the rows do not move under a thumb
// while voting continues underneath; a row leaves only when its move has actually folded.
const Moves = observer(function Moves({ mover }: { mover: MoverStore }) {
  if (mover.outstandingRows.length === 0) {
    return (
      <p className="p-8 text-center text-neutral-500">
        Nothing to move. The next list appears when the admin device computes the next tick.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {mover.outstandingRows.map((row) => (
        <li
          key={row.player}
          className="flex items-center gap-3 rounded-xl bg-neutral-900 p-2 ring-1 ring-neutral-800"
        >
          <span className="w-28 shrink-0 text-center text-6xl leading-none font-bold tabular-nums">
            {row.player}
          </span>
          <span className="flex flex-1 items-center gap-2">
            <Chip court={row.from} />
            <span aria-hidden className="text-2xl text-neutral-500">
              →
            </span>
            <Chip court={row.to} />
          </span>
          <button
            aria-label={`player ${row.player} moved to ${row.to}`}
            disabled={!mover.canMove}
            onClick={() => void mover.tickOff(row)}
            className="h-20 w-28 shrink-0 rounded-lg bg-emerald-700 text-xl font-bold text-white disabled:opacity-25"
          >
            MOVED
          </button>
        </li>
      ))}
    </ul>
  );
});

// Voting and marking on behalf of a coach who cannot work a phone, on the court this mover is
// standing on. Same two weights as the coach screen (docs/scoring.md): up +3, down -2. Same two
// flags, too, and the same faces on them, because a mover and a coach standing on the same court
// are doing the same job on the same players.
const CourtPanel = observer(function CourtPanel({ mover }: { mover: MoverStore }) {
  if (mover.court === undefined) {
    return <p className="p-8 text-center text-neutral-500">Tap the court you are working.</p>;
  }
  if (mover.courtRows.length === 0) {
    return <p className="p-8 text-center text-neutral-500">Nobody is on this court.</p>;
  }
  return (
    <ul className="flex flex-col gap-2">
      {mover.courtRows.map((row) => (
        <li
          key={row.number}
          className="flex items-center gap-2 rounded-xl bg-neutral-900 p-2 ring-1 ring-neutral-800"
        >
          {/* The score sits UNDER the number rather than beside it, as it does on the coach screen.
              Five targets a row need the width, and a column of two numbers reads the same at a
              glance as a line of two. */}
          <div className="flex w-20 shrink-0 flex-col items-center">
            <span className="text-4xl leading-none font-bold tabular-nums">{row.number}</span>
            <span className="text-base text-neutral-400 tabular-nums">{row.score}</span>
          </div>
          {/* `min-w-0` on the track and on every button: a grid item's default `min-width: auto`
              refuses to shrink below its own text, which is how a five-button row turns a phone
              into a sideways scroll. The e2e suite pins that at 360px. */}
          <div className="grid min-w-0 flex-1 grid-cols-5 gap-1.5">
            <Tap
              label={`upvote player ${row.number}`}
              face="+3"
              disabled={!mover.canVote}
              tone="bg-emerald-700 text-white"
              onTap={() => void mover.voteFor(row.number, 'up')}
            />
            <Tap
              label={`downvote player ${row.number}`}
              face="−2"
              disabled={!mover.canVote}
              tone="bg-rose-800 text-white"
              onTap={() => void mover.voteFor(row.number, 'down')}
            />
            {/* The two flags are toggles that show their own state: a mover has to be able to read
                whether player 7 is already marked BIG without tapping to find out, because tapping
                to find out is what sets it. */}
            <Tap
              label={`big, player ${row.number}`}
              face="BIG"
              pressed={row.big}
              disabled={!mover.canVote}
              tone={row.big ? 'bg-sky-500 text-neutral-900' : 'bg-neutral-800 text-neutral-400'}
              onTap={() => void mover.toggleBig(row.number)}
            />
            <Tap
              label={`lightbulb, player ${row.number}`}
              face="IQ"
              pressed={row.lightbulb}
              disabled={!mover.canVote}
              tone={
                row.lightbulb ? 'bg-amber-400 text-neutral-900' : 'bg-neutral-800 text-neutral-400'
              }
              onTap={() => void mover.toggleLightbulb(row.number)}
            />
            <Tap
              label={`player ${row.number} has left`}
              face="LEFT"
              disabled={!mover.canEditRoster}
              tone="bg-neutral-700 text-neutral-200"
              onTap={() => mover.askStrikeOff(row.number)}
            />
          </div>
        </li>
      ))}
    </ul>
  );
});

// Every player and their court, for the spot check a mover does between ticks. Movers see
// everything, unlike coaches (docs/user-journeys.md).
const Audit = observer(function Audit({ mover }: { mover: MoverStore }) {
  return (
    <div className="flex flex-col gap-3">
      {mover.returningRows.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm tracking-widest text-neutral-500 uppercase">gone home</h2>
          <ul className="flex flex-wrap gap-2">
            {mover.returningRows.map((player) => (
              <li key={player}>
                <button
                  aria-label={`player ${player} is back`}
                  disabled={!mover.canEditRoster}
                  onClick={() => mover.askReinstate(player)}
                  className="rounded-lg bg-neutral-800 px-4 py-3 text-2xl font-bold tabular-nums text-neutral-300 disabled:opacity-25"
                >
                  {player}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {mover.auditRows.map((row) => (
          <li
            key={row.number}
            className="flex items-center gap-2 rounded-lg bg-neutral-900 p-2 ring-1 ring-neutral-800"
          >
            <span className="flex-1 text-center text-3xl font-bold tabular-nums">{row.number}</span>
            <Chip court={row.court} />
            <button
              aria-label={`player ${row.number} has left`}
              disabled={!mover.canEditRoster}
              onClick={() => mover.askStrikeOff(row.number)}
              className="rounded bg-neutral-800 px-2 py-2 text-sm text-neutral-400 disabled:opacity-25"
            >
              LEFT
            </button>
          </li>
        ))}
      </ul>
      {mover.auditRows.length === 0 && (
        <p className="p-8 text-center text-neutral-500">Nobody is registered yet.</p>
      )}
    </div>
  );
});

// The two confirm flows. A strike-off takes a player off every coach screen in the hall and a
// reinstatement puts them back on a court the mover names, so neither happens on one tap of a row
// in a list somebody is reading while walking.
const Confirm = observer(function Confirm({ mover }: { mover: MoverStore }) {
  if (mover.strikeCandidate !== undefined) {
    const player = mover.strikeCandidate;
    return (
      <Bar>
        <span className="text-lg">
          Player <b className="text-3xl tabular-nums">{player}</b> has gone home?
        </span>
        <button
          onClick={() => void mover.strikeOff(player)}
          className="rounded-lg bg-rose-700 px-5 py-3 text-lg font-bold text-white"
        >
          Strike off
        </button>
        <CancelButton mover={mover} />
      </Bar>
    );
  }
  if (mover.reinstateCandidate !== undefined) {
    const player = mover.reinstateCandidate;
    return (
      <Bar>
        <span className="text-lg">
          Player <b className="text-3xl tabular-nums">{player}</b> is back. Which court?
        </span>
        {COURTS.map((court) => (
          <button
            key={court}
            onClick={() => void mover.reinstate(player, court)}
            className={`rounded-lg px-4 py-3 text-lg font-bold uppercase ${COURT_TONE[court]}`}
          >
            {court}
          </button>
        ))}
        <CancelButton mover={mover} />
      </Bar>
    );
  }
  return null;
});

const CancelButton = observer(function CancelButton({ mover }: { mover: MoverStore }) {
  return (
    <button onClick={mover.cancel} className="rounded-lg px-4 py-3 text-lg text-neutral-400">
      Cancel
    </button>
  );
});

function Bar({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alertdialog"
      className="flex flex-wrap items-center gap-2 rounded-xl bg-neutral-800 p-3 ring-1 ring-sky-500"
    >
      {children}
    </div>
  );
}

function Chip({ court }: { court: Court }) {
  return (
    <span className={`rounded px-2 py-1 text-sm font-bold uppercase ${COURT_TONE[court]}`}>
      {court}
    </span>
  );
}

// A target big enough for a thumb on a phone held one-handed while walking. Five of these share a
// row with the player number, so it is narrower than the coach screen's four and its face is sized
// to the longest one ("LEFT") rather than to the shortest.
//
// `aria-pressed` is what carries a toggle's state to a screen reader and to an e2e query; the
// colour carries it to a mover. It is omitted, rather than set false, on the three buttons that are
// not toggles: a `+3` that announced itself as "not pressed" would invite a mover to wonder what
// pressing it would latch.
function Tap({
  label,
  face,
  tone,
  disabled,
  pressed,
  onTap,
}: {
  label: string;
  face: string;
  tone: string;
  disabled: boolean;
  pressed?: boolean;
  onTap: () => void;
}) {
  return (
    <button
      aria-label={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      disabled={disabled}
      onClick={onTap}
      className={`h-14 min-w-0 overflow-hidden rounded-lg px-0 text-sm font-bold disabled:opacity-25 ${tone}`}
    >
      {face}
    </button>
  );
}
